/* global SendAdobeData, streetAndHouseNumberExpirementVariations */
import { getCartObjectJson } from '../../utils';
import {
  initOptimizely,
  optimizelyClient,
} from '../../services/optimizelyService';
import { logError } from '../../services/sentryService';

const ADDRESS_FOUND_CERTAIN_AND_NOT_CHANGED = '1';
const ADDRESS_FOUND_CERTAIN_BUT_CHANGED = '2';
const ADDRESS_FOUND_UNCERTAIN_AND_CHANGED = '3';
const NOT_CHECKED = 'NC';
const STREET_CHANGE = 'S';

const targetCountryCodes = ['DE', 'AT'];
const streetAndHouseNumberVariations =
  typeof streetAndHouseNumberExpirementVariations !== 'undefined'
    ? JSON.parse(streetAndHouseNumberExpirementVariations)
    : {};

let isSeparated = false;
let isTargetDeliveryCountrySelected = false;
let isShippingForm = true;

const isGoodAddress = (userAddress, suggestedAddress) => {
  if (!hasSuggestion(suggestedAddress)) return false;

  const uniservResultClass = suggestedAddress.out_res_post_cl;
  const uniservResultChangedParts = suggestedAddress.out_res_change;

  switch (uniservResultClass) {
    case ADDRESS_FOUND_CERTAIN_AND_NOT_CHANGED:
      return true;
    case ADDRESS_FOUND_CERTAIN_BUT_CHANGED:
    case ADDRESS_FOUND_UNCERTAIN_AND_CHANGED:
      if (
        uniservResultChangedParts !== NOT_CHECKED &&
        !uniservResultChangedParts.includes(STREET_CHANGE)
      ) {
        return true;
      }
      return hasOnlyCasingDifferences(userAddress, suggestedAddress);
    default:
      return false;
  }
};

const hasOnlyCasingDifferences = (userAddress, suggestedAddress) => {
  return (
    getUserStreetAndHouseNumber(userAddress).toLowerCase() ===
    suggestedAddress.out_str_hno.toLowerCase()
  );
};

const hasSuggestion = (suggestedAddress) => {
  return suggestedAddress instanceof Object && suggestedAddress.out_str_hno;
};

const getUserStreetAndHouseNumber = (userAddress) => {
  if (isSeparated) {
    return `${userAddress.street} ${userAddress.houseNumber}`;
  }

  return userAddress.street;
};

const getSuggestedStreetAndHouseNumber = (suggestedAddress) => {
  if (hasSuggestion(suggestedAddress)) {
    return suggestedAddress.out_str_hno;
  }

  return 'None';
};

const sendEventToOptimizely = (isAddressGood) => {
  try {
    if (!optimizelyClient) {
      initOptimizely();
    }

    optimizelyClient.onReady().then(() => {
      const cartData = getCartObjectJson();
      const domain = cartData && cartData.cart.domain;
      const user = optimizelyClient.createUserContext(window.optimizelyUserId, {
        domain,
      });

      if (user && isAddressGood) {
        user.trackEvent('GoodAddress');
      }
    });
  } catch (err) {
    logError('Unable to send event to Optimizely');
  }
};

const sendEventToAdobe = (
  userAddress,
  suggestedAddress,
  isSuggesstionAccepted
) => {
  try {
    let pageVariation = 'Account_Registration_Shipping_Not_Separated';
    if (isSeparated) {
      pageVariation = 'Account_Registration_Shipping_Separated';
    }

    let addressData = 'Good_Add/';
    if (!isGoodAddress(userAddress, suggestedAddress)) {
      addressData = 'Bad_Add/';
    }

    addressData += `Original=${getUserStreetAndHouseNumber(
      userAddress
    )}/Suggested=${getSuggestedStreetAndHouseNumber(
      suggestedAddress
    )}/Selected=${isSuggesstionAccepted}`;

    if (typeof SendAdobeData === 'function') {
      SendAdobeData('customClickTrack', 'Checkout', pageVariation, addressData);
    }
  } catch (err) {
    logError('Unable to send event to Adobe Analytics');
  }
};

export const StreetAndHouseNumberSeparationUtils = {
  setSeparationFlag(selectedCountryCode) {
    if (isShippingForm) {
      isTargetDeliveryCountrySelected =
        targetCountryCodes.includes(selectedCountryCode);

      if (
        window.isStreetAndHouseNumberSeparationEnabled &&
        streetAndHouseNumberVariations.variant &&
        isTargetDeliveryCountrySelected
      ) {
        isSeparated = true;
      } else {
        isSeparated = false;
      }
    }
  },
  setIsShippingForm(currentForm) {
    isShippingForm = currentForm === 'shipping';
  },
  isSeparated() {
    return isSeparated;
  },
  getStreetAndHouseNumber(userAddress) {
    return getUserStreetAndHouseNumber(userAddress);
  },
  getUniservRequestParams(userAddress) {
    const street = userAddress.street.toLowerCase();

    if (this.isSeparated() && isShippingForm) {
      let houseNumber = userAddress.houseNumber.toLowerCase();

      return {
        in_str: street,
        in_hno: houseNumber,
      };
    }

    return {
      in_str_hno: street,
    };
  },
  sendEventsToAdobeAndOptimizely({
    userAddress,
    suggestedAddress,
    isSuggesstionAccepted,
    formType,
  }) {
    if (formType !== 'shipping') return;

    if (
      window.isStreetAndHouseNumberSeparationEnabled &&
      isTargetDeliveryCountrySelected
    ) {
      sendEventToOptimizely(isGoodAddress(userAddress, suggestedAddress));
      sendEventToAdobe(userAddress, suggestedAddress, isSuggesstionAccepted);
    }
  },
};
